import { router } from "@inertiajs/react"
import { Fragment, useEffect, useRef, useState } from "react"
import type { ChangeEvent, MouseEvent, RefObject } from "react"

import ImageOverlay from "./ImageOverlay"
import type { ImageOverlayLabels } from "./ImageOverlay"

// t030 impl-section (plan Extraction B): lifted out of `Images.tsx`
// verbatim -- exported here (not there) since the grid/overlay row shape
// is intrinsically this component's own now; `Images.tsx` and
// `ImageOverlay.tsx` both import it from here.
export interface ImageItem {
  id: number
  // FR-7.1, FR-7.4, AC-9: the framework's own signed redirect routes at
  // the default -- no URL resolving to the original blob is ever
  // composed, here or anywhere else in this domain.
  thumbUrl: string
  fullUrl: string
  // FR-10.4: "{uploader}, {date}", composed server-side -- this page
  // formats no date of its own.
  alt: string
  // FR-10.6: the overlay's own top bar reads this alone, separately
  // from `alt` above.
  uploaderName: string
  // FR-10.5: "9 сен, 14:32", composed server-side in the trip's own
  // zone -- consumed by the overlay, not this page's own grid.
  stampText: string
  // FR-9.6: a server-computed per-image permission flag -- consumed by
  // the overlay's delete control, not this page's own grid.
  canDelete: boolean
  // t030 impl-confirm (FR-6.3, FR-6.4): the picture's total day count
  // across the trip, from one bounded aggregate query server-side --
  // consumed by the overlay's delete confirm, not this page's own grid.
  dayCount: number
  // t030 impl-confirm (FR-6.1): the confirm's already-pluralized second
  // line ("Она привязана к N дням."), resolved server-side since there
  // is no I18n on the client to pick a Russian plural form from a bare
  // number -- `null` at `dayCount` 0 (FR-6.2's byte-identical-at-zero
  // rule).
  attachedText: string | null
  // t031 impl-read (FR-6.1, FR-6.2): the set of trip day ids this picture
  // is attached to, from one grouped query server-side -- `dayCount`
  // follows from this set's size rather than being counted separately.
  // Consumed by the overlay's days mode (a later child); this child only
  // lands the field.
  dayIds: number[]
}

// t031 impl-read (FR-3.2, FR-3.4): one row of the overlay's days-mode day
// list, composed server-side by `Schedule::ListDayOptions` -- this page
// formats no date and no weekday of its own.
export interface DayOption {
  id: number
  shortDate: string
  text: string
}

// t030 impl-section (FR-3.4): the whole prop bundle
// `Images::ImagePresentation#images_section_props` composes, shared
// verbatim by the tab (`Images.tsx`) and the day page (`Day.tsx`) --
// `labels` omits `empty` on purpose, since a surface that wants the
// empty-state line supplies it explicitly through `emptyLine` instead
// (FR-3.3: the day page passes `null`, so it renders no line at all).
export interface ImagesSectionProps {
  items: ImageItem[]
  countText: string | null
  labels: { add: string; uploading: string } & ImageOverlayLabels
  uploadPath: string
  // t031 impl-read (FR-3.5): the trip's day list for the overlay's
  // days-mode row list -- the same bundle on both surfaces, composed by
  // this shared concern rather than assembled separately per page.
  // Rendered by a later child; this child only lands the field and its
  // pass-through.
  days: DayOption[]
}

interface ImagesSectionOwnProps extends ImagesSectionProps {
  // FR-3.3: `null` renders no empty-state line at all (the day page's
  // "no grid, no empty-state illustration"); a string renders it (the
  // tab's own `labels.empty`).
  emptyLine: string | null
  // Owner ruling 2026-09-13 (docs/design/day-page.html state 1): where
  // the dashed upload button sits relative to the grid -- `top` on the
  // tab (the library is filled from there), `bottom` on the day page
  // (the pictures are the content, the button the afterthought). The
  // refused-file lines follow the button wherever it sits; nothing else
  // differs between the two placements.
  uploadPlacement?: "top" | "bottom"
}

const UPLOAD_BUTTON_BASE_CLASSES =
  "flex h-11 items-center justify-center gap-1.5 rounded-xl border-[1.5px] " +
  "border-dashed border-gray-300 text-sm font-medium"

// FR-4.3: "Загружаем 2 из 3…" -- `%{done}` is the file currently in
// flight (one-based, so the first file in a batch reads "1 из N", not
// "0 из N"), `%{total}` the batch size. The `LinksFieldset#
// formatRowHeading` idiom: a plain `String#replace` pair, not a
// general-purpose interpolation helper, since this one template only
// ever carries these two placeholders.
function formatUploadingLabel(template: string, done: number, total: number): string {
  return template.replace("%{done}", String(done)).replace("%{total}", String(total))
}

interface UploadButtonProps {
  label: string
  disabled: boolean
  onFilesSelected: (files: File[]) => void
  // t031 impl-focus-docs (FR-7.2): the post-delete focus rule's own
  // fallback target when the grid is left empty -- named `inputRef`,
  // not `ref`, since this component is a plain function, not wrapped in
  // `forwardRef` (no other caller needs the DOM node, so the smaller
  // surface wins over the extra ceremony).
  inputRef: RefObject<HTMLInputElement | null>
}

// FR-4.5, FR-10.1, FR-10.3: the upload button, docs/design/images.html
// Block 1/2/3 verbatim, minus its own pointer-cursor utility class --
// restored application-wide by `app/shared/entrypoints/application.css`'s
// own `label:has(> input[type="file"]...)` rule instead (the
// `spec/stylesheet_spec.rb` AC-12 gate's landed convention) -- a <label>
// around a visually hidden multi-file input, `accept="image/*"` verbatim,
// permitting several files at once. FR-4.2: disabled (and
// `aria-disabled`, Block 2's own `text-gray-400` ink) for the whole
// duration of a batch, so a second selection cannot start a second batch
// concurrently with the first.
function UploadButton({ label, disabled, onFilesSelected, inputRef }: UploadButtonProps) {
  function handleChange(event: ChangeEvent<HTMLInputElement>) {
    const files = Array.from(event.target.files ?? [])
    // Reset so selecting the exact same file(s) again still fires this
    // handler -- the browser does not re-fire `change` for an unchanged
    // <input> value otherwise.
    event.target.value = ""
    if (files.length > 0) onFilesSelected(files)
  }

  return (
    <label
      className={`${UPLOAD_BUTTON_BASE_CLASSES} ${disabled ? "text-gray-400" : "text-accent"}`}
      aria-disabled={disabled || undefined}
    >
      {label}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        className="sr-only"
        disabled={disabled}
        onChange={handleChange}
      />
    </label>
  )
}

// FR-4.3, contract Block 2: one placeholder cell per file not yet
// settled, at the top of the grid (newest-first, where a finished
// file's real thumbnail will land) -- the first (in-flight) carries the
// progress bar `onProgress` drives; the rest are plain, bar-less
// `aria-busy` cells.
function PlaceholderCell({ inFlight, progress }: { inFlight: boolean; progress: number }) {
  return (
    <li
      className={`aspect-square rounded-[10px] bg-gray-100 ${inFlight ? "relative" : ""}`}
      aria-busy="true"
    >
      {inFlight && (
        <div className="absolute inset-x-2.5 bottom-2.5 h-1 overflow-hidden rounded-full bg-gray-200">
          <div className="h-full bg-accent" style={{ width: `${progress}%` }} />
        </div>
      )}
    </li>
  )
}

// t030 impl-section (plan Extraction B): the shared body both the
// «Картинки» tab (`Images.tsx`) and the day page's own last section
// (`Day.tsx`) render -- the counter line, the dashed upload button, any
// failed lines and the 3-column grid, plus the overlay it opens. Returns
// a **fragment**, not a wrapper element, so each caller supplies its own
// surrounding element: the tab's `Layout` (its three former direct
// children stay direct children of `Layout`'s own `flex flex-col gap-4`)
// and the day page's own `<section>` (docs/design/day-page.html:304).
//
// FR-4.1, FR-4.2: one `router.post` per selected file, strictly
// sequential -- the next request starts only once the previous one has
// settled, success or refusal alike, so a refusal mid-batch never aborts
// the files still queued behind it. `preserveState: true` is
// load-bearing: without it, each successful visit would remount this
// component and wipe the batch's own queue/progress/failed-line state
// mid-batch (the framework's own default is to replace the component on
// every visit). `forceFormData: true` because the payload carries a
// real `File`, and `preserveScroll: true` so a mid-page upload never
// jumps the viewport back to the top. `uploadPath` generalizes the
// address this batch posts to -- "/images" for the tab, "/days/:date/
// images" for the day page (FR-4.1).
//
// FR-6.4: failed lines are this component's own state, appended in
// `onError` and read from nowhere else -- never rendered from Inertia's
// own `errors` prop directly, since that prop would otherwise be
// replaced (and so silently cleared) by the very next visit in the same
// batch. Reset once, at the start of the next batch (FR-6.4's own
// "cleared when the next batch starts") -- never by a timer.
export default function ImagesSection({
  items,
  countText,
  labels,
  uploadPath,
  emptyLine,
  days,
  uploadPlacement = "top",
}: ImagesSectionOwnProps) {
  const [total, setTotal] = useState(0)
  const [done, setDone] = useState(0)
  const [uploading, setUploading] = useState(false)
  const [progress, setProgress] = useState(0)
  const [failedLines, setFailedLines] = useState<string[]>([])
  // FR-10.6: the overlay's own open state -- the index into `items`, or
  // `null` when closed. Component state, not a route (navigating away
  // closes it by unmount). `triggerRef` remembers which cell opened it,
  // the `Layout.tsx` burger-trigger idiom, so focus can return there on
  // an ordinary close -- the overlay itself only ever focuses inward,
  // never back out.
  const [openIndex, setOpenIndex] = useState<number | null>(null)
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  // t031 impl-focus-docs (FR-7.2): `gridRef` and `uploadInputRef` are the
  // two candidate post-delete focus targets; `pendingDeleteIndexRef`
  // carries the deleted picture's own vacated index across to the effect
  // below, which is what actually performs the focus once the DOM
  // reflects the shorter `items` array `preserveState` (ImageOverlay's
  // own delete visit) lets this component live to see.
  const gridRef = useRef<HTMLUListElement | null>(null)
  const uploadInputRef = useRef<HTMLInputElement | null>(null)
  const pendingDeleteIndexRef = useRef<number | null>(null)
  // PR #36 review (FR-7.2): an ordinary close can find its trigger gone
  // too -- detaching the day a day-page overlay was opened from drops
  // that picture out of `items` while the overlay stays open on its own
  // snapshot, so the cell that opened it has already left the DOM by the
  // time ×/«Готово»/Escape fire. Focusing a detached node is a no-op that
  // strands focus on `document.body`; the index is stashed here for the
  // same clamped fallback the delete path uses, run once the dialog has
  // unmounted.
  const pendingCloseIndexRef = useRef<number | null>(null)

  function openOverlayAt(index: number, event: MouseEvent<HTMLButtonElement>) {
    triggerRef.current = event.currentTarget
    setOpenIndex(index)
  }

  // t031 impl-focus-docs (FR-7.2): `deleted` distinguishes the two exits
  // `ImageOverlay` may report. An ordinary close (×, «Готово», Escape)
  // keeps the old rule while `triggerRef` is still a live node; a detach
  // of the current day has already removed that node (above), so the
  // fallback takes over. A delete's own vacated cell is gone only once
  // the DOM catches up with the shorter `items`, so `triggerRef` cannot
  // be trusted there either -- the index is stashed for the effect below
  // instead, which runs once that DOM update has landed.
  function closeOverlay(deleted?: boolean) {
    if (deleted) {
      pendingDeleteIndexRef.current = openIndex
      setOpenIndex(null)
      return
    }

    if (!triggerRef.current?.isConnected) {
      pendingCloseIndexRef.current = openIndex
    }
    setOpenIndex(null)
    triggerRef.current?.focus()
  }

  // Reads the grid's own real `<button>`s (never the placeholder cells
  // above, which carry none) rather than trusting `items.length` alone,
  // so the count this clamps against is what is actually on screen. An
  // empty grid focuses the upload control instead -- never `document.body`.
  // Returns whether anything was focused: while a batch is in flight the
  // input is `disabled` and a browser ignores `focus()` on it (PR #36
  // review), so the caller keeps its pending index and tries again once
  // the batch has settled.
  function focusCellOrUpload(index: number): boolean {
    const cellButtons = gridRef.current?.querySelectorAll<HTMLButtonElement>("button") ?? []
    if (cellButtons.length > 0) {
      cellButtons[Math.min(index, cellButtons.length - 1)]?.focus()
      return true
    }

    const input = uploadInputRef.current
    if (!input || input.disabled) return false
    input.focus()
    return true
  }

  // t031 impl-focus-docs (FR-7.2, AC-11): fires once the DOM reflects
  // whatever `items` the delete's own response sent back -- exactly the
  // render `pendingDeleteIndexRef` was waiting for. `uploading` is a
  // dependency too, so a fallback deferred by an in-flight batch lands
  // on the render that re-enables the upload control.
  useEffect(() => {
    if (pendingDeleteIndexRef.current === null) return
    if (!focusCellOrUpload(pendingDeleteIndexRef.current)) return

    pendingDeleteIndexRef.current = null
  }, [items, uploading])

  // PR #36 review (FR-7.2): the ordinary-close counterpart -- the grid is
  // already current, so the only render to wait for is the dialog's own
  // unmount, i.e. `openIndex` going back to `null` (or, deferred the same
  // way, the batch settling).
  useEffect(() => {
    if (openIndex !== null || pendingCloseIndexRef.current === null) return
    if (!focusCellOrUpload(pendingCloseIndexRef.current)) return

    pendingCloseIndexRef.current = null
  }, [openIndex, items, uploading])

  function uploadOne(file: File): Promise<void> {
    return new Promise((resolve) => {
      router.post(
        uploadPath,
        { file },
        {
          forceFormData: true,
          preserveState: true,
          preserveScroll: true,
          onProgress: (event) => setProgress(event?.percentage ?? 0),
          onError: (errors) => {
            if (errors.file) setFailedLines((lines) => [...lines, errors.file])
          },
          onFinish: () => resolve(),
        },
      )
    })
  }

  async function runBatch(files: File[]) {
    setFailedLines([])
    setTotal(files.length)
    setDone(0)
    setProgress(0)
    setUploading(true)

    // Strictly sequential by design (FR-4.2): the next request must not
    // start until this one has settled, so each iteration's await is
    // deliberate, not an oversight a lint rule would otherwise catch.
    for (const file of files) {
      await uploadOne(file)
      setDone((value) => value + 1)
      setProgress(0)
    }

    setUploading(false)
  }

  const pendingCount = uploading ? total - done : 0
  const buttonLabel = uploading
    ? formatUploadingLabel(labels.uploading, Math.min(done + 1, total), total)
    : labels.add

  // The button and its refused-file lines travel together: above the
  // grid on the tab, below it on the day page (`uploadPlacement`). Both
  // are fragments of the same flat list, so each caller's own wrapper
  // (`Layout`'s `gap-4`, the day page's `<section>` `gap-3`) still
  // spaces every piece as a direct child.
  const uploadControl = (
    <Fragment>
      <UploadButton
        label={buttonLabel}
        disabled={uploading}
        onFilesSelected={runBatch}
        inputRef={uploadInputRef}
      />
      {/* Plain refusal strings with no identity of their own, and can
          repeat verbatim (two files can fail for the same reason); the
          list is append-only within one batch and cleared wholesale at
          the next, so an index key is stable for its own lifetime. */}
      {failedLines.map((line, index) => (
        <p key={index} className="text-xs leading-4 text-red-700">
          {line}
        </p>
      ))}
    </Fragment>
  )

  // Upload placeholders stay at the head of the grid whichever side the
  // button is on -- newest first is where the finished file lands.
  const grid =
    items.length > 0 || pendingCount > 0 ? (
      <ul ref={gridRef} className="grid grid-cols-3 gap-1.5">
        {Array.from({ length: pendingCount }, (_, index) => (
          <PlaceholderCell key={`pending-${index}`} inFlight={index === 0} progress={progress} />
        ))}
        {items.map((item, index) => (
          <li key={item.id}>
            <button
              type="button"
              className="block aspect-square w-full overflow-hidden rounded-[10px] bg-gray-100"
              onClick={(event) => openOverlayAt(index, event)}
            >
              <img src={item.thumbUrl} alt={item.alt} className="h-full w-full object-cover" />
            </button>
          </li>
        ))}
      </ul>
    ) : (
      // FR-3.3: no empty-state illustration at all when `emptyLine` is
      // `null` -- the day page's own empty day is "the button alone".
      emptyLine !== null && <p className="py-10 text-center text-sm text-gray-400">{emptyLine}</p>
    )

  return (
    <Fragment>
      {countText !== null && <p className="text-sm text-gray-500">{countText}</p>}
      {uploadPlacement === "top" ? (
        <Fragment>
          {uploadControl}
          {grid}
        </Fragment>
      ) : (
        <Fragment>
          {grid}
          {uploadControl}
        </Fragment>
      )}
      {openIndex !== null && (
        <ImageOverlay
          images={items}
          index={openIndex}
          onClose={closeOverlay}
          onNavigate={setOpenIndex}
          labels={labels}
          days={days}
        />
      )}
    </Fragment>
  )
}
